import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton, useTheme, Modal, Chip, Divider, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import QuestionView from './QuestionView';
import ResultsView from './ResultsView';

interface Question {
    idQuestion: string;
    type: string;
    question: string;
    A: string;
    B: string;
    C: string;
    D: string;
    E?: string;
    correct: string;
}

interface QuizModalProps {
    category: string;
    numQuestions: number;
    questions: Question[];
    setOpen: (open: boolean) => void;
}

const QuizModal: React.FC<QuizModalProps> = ({ category, numQuestions, questions, setOpen }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [shuffledQuestions, setShuffledQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<{ [key: string]: string }>({});
    const [showResults, setShowResults] = useState(false);
    const [timer, setTimer] = useState(900); // 30 minutos em segundos
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState<(() => void) | null>(null);

    useEffect(() => {
        const shuffled = [...questions].sort(() => Math.random() - 0.5).slice(0, numQuestions);
        setShuffledQuestions(shuffled);
    }, [questions, numQuestions]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimer((prevTimer) => {
                if (prevTimer <= 0) {
                    clearInterval(interval);
                    setShowResults(true);
                    return 0;
                }
                return prevTimer - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const handleChange = (idQuestion: string, answer: string) => {
        setAnswers({
            ...answers,
            [idQuestion]: answer
        });
    };

    const handleNext = () => {
        setCurrentQuestion((prev) => (prev + 1 < numQuestions ? prev + 1 : prev));
    };

    const handlePrev = () => {
        setCurrentQuestion((prev) => (prev - 1 >= 0 ? prev - 1 : prev));
    };

    const handleDialogClose = (action?: () => void) => {
        setDialogOpen(false);
        if (action) action();
    };

    const handleSubmit = () => {
        setDialogAction(() => () => setShowResults(true));
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogAction(() => () => setOpen(false));
        setDialogOpen(true);
    };

    const score = Object.keys(answers).reduce(
        (score, idQuestion) => (answers[idQuestion] === shuffledQuestions.find((q) => q.idQuestion === idQuestion)?.correct ? score + 1 : score),
        0
    );

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const getTimeColor = (seconds: number) => {
        if (seconds <= 300) return 'error';
        if (seconds <= 600) return 'warning';
        return 'success';
    };

    return (
        <>
            <Modal open={true} onClose={handleClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{
                    width: '80%',
                    maxHeight: '90%',
                    bgcolor: theme.palette.background.paper,
                    boxShadow: 24,
                    p: theme.spacing(2),
                    borderRadius: theme.shape.borderRadius,
                    overflow: 'auto'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                            {category}
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                    {!showResults && !isMobile && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
                            {shuffledQuestions.map((_, index) => (
                                <Button
                                    key={index}
                                    variant="contained"
                                    onClick={() => setCurrentQuestion(index)}
                                    sx={{ backgroundColor: currentQuestion === index ? theme.palette.primary.main : theme.palette.grey[300] }}
                                >
                                    {index + 1}
                                </Button>
                            ))}
                        </Box>
                    )}
                    {!showResults ? (
                        shuffledQuestions[currentQuestion] && (
                            <QuestionView
                                question={shuffledQuestions[currentQuestion]}
                                currentQuestionIndex={currentQuestion}
                                totalQuestions={numQuestions}
                                answer={answers[shuffledQuestions[currentQuestion].idQuestion] || ''}
                                onChangeAnswer={handleChange}
                                onPrev={handlePrev}
                                onNext={handleNext}
                            />
                        )
                    ) : (
                        <ResultsView
                            questions={shuffledQuestions}
                            answers={answers}
                            score={score}
                            totalQuestions={numQuestions}
                            onClose={() => setOpen(false)}
                        />
                    )}
                    {!showResults && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <Chip label={formatTime(timer)} color={getTimeColor(timer)} />
                            <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: theme.palette.primary.main, color: 'white' }}>
                                Finalizar
                            </Button>
                        </Box>
                    )}
                </Box>
            </Modal>
            <Dialog open={dialogOpen} onClose={() => handleDialogClose()}>
                <DialogTitle>Confirmação</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialogAction === handleClose
                            ? 'Deseja abandonar a prova?'
                            : 'Deseja realmente finalizar a prova?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleDialogClose()}>Cancelar</Button>
                    <Button onClick={() => handleDialogClose(dialogAction!)} autoFocus>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default QuizModal;
